import { Form, Formik } from "formik";
import { usePostHog } from "posthog-js/react";
import { FC } from "react";
import { object, string } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { PostHogEvent } from "@/shared/types";
import { Button, Input, Typography } from "@/shared/ui";
import { useOnboardingStore } from "@/stores";

import styles from "./activity.module.scss";
import { ActivityTabFormScheme } from "./activity.types";

const validationScheme = object({
  occupation: string().max(75, "Must be at most 75 characters").optional(),
  locationWorkplace: string().max(75, "Must be at most 75 characters").optional(),
});

const ActivityTab: FC = () => {
  const posthog = usePostHog();
  const currentStep = useOnboardingStore((state) => state.currentStep);
  const isFirstPassingStep = useOnboardingStore((state) => !state.passedSteps.includes(currentStep));
  const { setCurrentStep, setInfo, addPassedStep } = useOnboardingStore((state) => state.actions);

  const occupation = useOnboardingStore((state) => state.userInfo.occupation);
  const locationWorkplace = useOnboardingStore((state) => state.userInfo.locationWorkplace);

  const onSubmitHandler = (data: ActivityTabFormScheme) => {
    if (isFirstPassingStep) {
      posthog?.capture(PostHogEvent.PERSONAL_DETAILS_SUBMITTED);
    }
    setInfo({
      occupation: data.occupation || undefined,
      locationWorkplace: data.locationWorkplace || undefined,
    });
    setCurrentStep(currentStep + 1);
    addPassedStep(currentStep);
  };

  const onSkipHandler = () => {
    if (isFirstPassingStep) {
      posthog?.capture(PostHogEvent.PERSONAL_DETAILS_SUBMITTED);
    }
    setCurrentStep(currentStep + 1);
    addPassedStep(currentStep);
  };

  return (
    <main className={styles.activityTab}>
      <Typography semi variant="title-2">
        What do you do for work?
      </Typography>

      <Formik<ActivityTabFormScheme>
        initialValues={{ occupation: occupation ?? "", locationWorkplace: locationWorkplace ?? "" }}
        validationSchema={toFormikValidationSchema(validationScheme)}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={onSubmitHandler}
      >
        {({ values, errors, setFieldValue, validateField }) => (
          <Form className={styles.form}>
            <Input
              placeholder="Occupation"
              variant={errors.occupation ? "error" : undefined}
              message={errors.occupation}
              value={values.occupation}
              onBlur={() => validateField("occupation")}
              onValueChange={async (value) => {
                await setFieldValue("occupation", value);
                await validateField("occupation");
              }}
            />
            <Input
              placeholder="Place of work"
              variant={errors.locationWorkplace ? "error" : undefined}
              message={errors.locationWorkplace}
              value={values.locationWorkplace}
              onBlur={() => validateField("locationWorkplace")}
              onValueChange={async (value) => {
                await setFieldValue("locationWorkplace", value);
                await validateField("locationWorkplace");
              }}
            />

            <Button
              fullWidth
              size="big"
              type="submit"
              disabled={!values.locationWorkplace || !values.occupation || !!Object.values(errors).length}
            >
              Continue
            </Button>
            <Button fullWidth size="big" type="button" variant="skip1" onClick={onSkipHandler}>
              Skip
            </Button>
          </Form>
        )}
      </Formik>
    </main>
  );
};

export { ActivityTab };
